// contexts/AuthContext.js
// ניהול אימות — login, logout, token, user state
//
// ★ Token נשמר ב-memory (דרך api.js setToken) — לא localStorage!
// ★ 2FA: response.data.data.details.requires_2fa (לא response.data.requires_2fa)
// ★ is_default_password (login) vs using_default_password (auth/check) — נורמול לשדה אחד

import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api, {
  setToken,
  clearToken,
  setAuthCallbacks,
  is2FARequired,
  get2FAMessage,
  extractErrorMessage,
} from '../services/api';

// =============================================================================
// Context
// =============================================================================
const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// =============================================================================
// Provider
// =============================================================================
export function AuthProvider({ children }) {
  const navigate = useNavigate();

  // State
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // טעינה ראשונית — בודק token
  const [isDefaultPassword, setIsDefaultPassword] = useState(false);
  const [debugMode, setDebugMode] = useState(false);

  // Ref למניעת race conditions ב-auth check
  const authCheckInProgress = useRef(false);

  // ===================================================================
  // handleLogout — ניקוי state + redirect
  // ===================================================================
  const handleLogout = useCallback(async (silent = false) => {
    // אם יש token, נסה לשלוח logout לשרת
    if (!silent) {
      try {
        await api.post('/auth/logout');
      } catch (err) {
        // אם logout נכשל (token כבר פג) — לא קריטי
      }
    }

    clearToken();
    setUser(null);
    setIsAuthenticated(false);
    setIsDefaultPassword(false);
    setDebugMode(false);
    navigate('/login');
  }, [navigate]);

  // ===================================================================
  // רישום callbacks ב-api.js — token expired / refreshed
  // ===================================================================
  useEffect(() => {
    setAuthCallbacks({
      onTokenExpired: () => {
        // Token פג — redirect to login
        setUser(null);
        setIsAuthenticated(false);
        setIsDefaultPassword(false);
        navigate('/login');
      },
      onTokenRefreshed: (newToken) => {
        // Token התחדש — לא צריך לעשות כלום, api.js כבר עדכן
        // אפשר להוסיף לוג אם צריך
      },
    });

    // Cleanup
    return () => {
      setAuthCallbacks({ onTokenExpired: null, onTokenRefreshed: null });
    };
  }, [navigate]);

  // ===================================================================
  // checkAuth — בדיקת token בטעינה ראשונית
  // GET /auth/check → { user, using_default_password, debug_mode }
  // ===================================================================
  const checkAuth = useCallback(async () => {
    if (authCheckInProgress.current) return;
    authCheckInProgress.current = true;

    try {
      const response = await api.get('/auth/check');
      if (response.data?.success && response.data?.data?.user) {
        const data = response.data.data;
        setUser(data.user);
        setIsAuthenticated(true);
        // ★ auth/check uses "using_default_password" (not "is_default_password")
        setIsDefaultPassword(data.using_default_password === true);
        setDebugMode(data.debug_mode === true);
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (err) {
      // 401 = token פג, אין token, וכו' — לא מחובר
      setIsAuthenticated(false);
      setUser(null);
      clearToken();
    } finally {
      setIsLoading(false);
      authCheckInProgress.current = false;
    }
  }, []);

  // בדיקה ראשונית בעליית הקומפוננטה
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ===================================================================
  // login — POST /auth/login
  // ===================================================================
  const login = useCallback(async (username, password, password2 = null, debug = false) => {
    const body = { username, password };
    if (password2) body.password2 = password2;
    if (debug) body.debug = true;

    try {
      const response = await api.post('/auth/login', body);

      if (response.data?.success && response.data?.data?.token) {
        const data = response.data.data;

        // שמירת token ב-memory (דרך api.js)
        setToken(data.token);

        // עדכון state
        setUser(data.user);
        setIsAuthenticated(true);
        // ★ login uses "is_default_password" (not "using_default_password")
        setIsDefaultPassword(data.is_default_password === true);
        setDebugMode(data.debug_mode === true);

        return {
          success: true,
          user: data.user,
          isDefaultPassword: data.is_default_password === true,
          message: response.data.message,
        };
      }

      // Response לא צפוי
      return {
        success: false,
        error: 'תגובה לא צפויה מהשרת',
      };
    } catch (err) {
      // ★ בדיקת 2FA — מבנה מיוחד!
      // 403 עם response.data.data.details.requires_2fa === true
      if (is2FARequired(err)) {
        return {
          success: false,
          requires2FA: true,
          message: get2FAMessage(err),
        };
      }

      // שגיאות אחרות
      return {
        success: false,
        requires2FA: false,
        error: extractErrorMessage(err, 'שגיאה בהתחברות'),
        status: err.response?.status,
      };
    }
  }, []);

  // ===================================================================
  // logout — POST /auth/logout
  // ===================================================================
  const logout = useCallback(async () => {
    await handleLogout(false);
  }, [handleLogout]);

  // ===================================================================
  // changePassword — POST /auth/change-password
  // ===================================================================
  const changePassword = useCallback(async (oldPassword, newPassword, newPasswordSecondary = null) => {
    const body = {
      old_password: oldPassword,
      new_password: newPassword,
    };
    if (newPasswordSecondary) {
      body.new_password_secondary = newPasswordSecondary;
    }

    try {
      const response = await api.post('/auth/change-password', body);
      if (response.data?.success) {
        setIsDefaultPassword(false);
        return { success: true, message: response.data.message };
      }
      return { success: false, error: 'תגובה לא צפויה' };
    } catch (err) {
      return {
        success: false,
        error: extractErrorMessage(err, 'שגיאה בשינוי סיסמה'),
      };
    }
  }, []);

  // ===================================================================
  // Helper: בדיקת הרשאות
  // ===================================================================
  const isAdmin = user?.is_admin === true;
  const isSuperAdmin = user?.is_super_admin === true;
  const hasViewRestriction = user?.view_restriction === true;

  // ===================================================================
  // Test Harness — register auth data for getState()
  // ===================================================================
  useEffect(() => {
    if (window.__MADAD_TEST__?._setAuthData) {
      window.__MADAD_TEST__._setAuthData(user, user ? 'token-exists' : null);
    }
  }, [user]);

  // Test Harness — expose login/logout for direct API calls (bypass UI)
  useEffect(() => {
    if (window.__MADAD_TEST__) {
      window.__MADAD_TEST__._authLogin = login;
      window.__MADAD_TEST__._authLogout = handleLogout;
    }
    return () => {
      if (window.__MADAD_TEST__) {
        delete window.__MADAD_TEST__._authLogin;
        delete window.__MADAD_TEST__._authLogout;
      }
    };
  }, [login, handleLogout]);

  // ===================================================================
  // Context value
  // ===================================================================
  const value = {
    // State
    user,
    isAuthenticated,
    isLoading,
    isDefaultPassword,
    debugMode,

    // Role helpers
    isAdmin,
    isSuperAdmin,
    hasViewRestriction,

    // Actions
    login,
    logout,
    changePassword,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
